<template>
    <p-modal
        @ok="exportCourseListReport"
        hide="reportToExport = null"
        size="lg"
        name="exportCourseListModal"
        :label="exportCourseListLabel"
        ref="exportCourseListModal"
        @show="initialize"
    >
        <template v-slot:default>
            <p-form ref="courseListForm">
                <div class="container-fluid content-wrapper">
                    <b-row cols="1" cols-md="2">
                        <b-col>
                            <p-checkbox
                                label="Include Retail Prices"
                                v-model="
                                    courseListParameters.includeRetailPrices
                                "
                            />
                            <p-checkbox
                                label="Include Stock On Hand"
                                v-model="
                                    courseListParameters.includeStockOnHand
                                "
                            />
                            <p-checkbox
                                label="Include QTP"
                                v-model="courseListParameters.includeQtp"
                            />
                            <p-checkbox
                                label="Include Enrollment (Estimated/Actual)"
                                v-model="courseListParameters.includeEnrollment"
                            />
                            <p-checkbox
                                label="Include Price Disclaimer"
                                v-model="
                                    courseListParameters.includePriceDisclaimer
                                "
                            />
                            <p-checkbox
                                label="Include ISBNs"
                                v-model="courseListParameters.includeIsbn"
                            />
                            <p-checkbox
                                label="New Page Per Department"
                                v-model="
                                    courseListParameters.newPagePerDepartment
                                "
                            />
                        </b-col>
                        <b-col>
                            
                            <p-advanced-select
                                v-if="!termId"
                                v-model="selectedTermId"
                                label="Term"
                                data-type="terms"
                                rules="required"
                            ></p-advanced-select>
                            <p-card class="mb-2 ps-2 py-2" no-body>
                                <label
                                    class="text-sm"
                                    v-if="courseSectionSelectHelpText"
                                    >{{ courseSectionSelectHelpText }}</label
                                >
                                <course-sections-selector
                                    ref="courseSectionsSelector"
                                    class="pt-2"
                                    @selection-change="
                                        courseListParameters.courseSections = $event
                                    "
                                    :course-request="mockCourseRequest"
                                    filter-course-data
                                    :select-filtered-data="false"
                                    :show-closed-term-option="false"
                                    :show-expand-all-option="true"
                                />
                            </p-card>
                        </b-col>
                    </b-row>
                    <UserReportTemplate 
                        :user-report-template-options="userReportTemplateOptions"
                        v-model:selected-user-report-template-id="selectedUserReportTemplateId"
                        :selected-user-report-template="selectedUserReportTemplate"
                        v-model:template-name="templateName"
                        @selected-object="applyUserReportTemplate($event)" />
                </div>
            </p-form>
        </template>
        <template v-slot:modal-footer="{ ok, cancel }">
            <b-row>
                <b-col>
                    <p-button :is-busy="isBusy" variant="outline-primary" @click="cancel">Cancel</p-button>
                    <p-button :is-busy="isBusy" variant="primary" @click="ok" class="ms-2">OK</p-button>
                </b-col>
            </b-row>
        </template>
    </p-modal>
        
</template>

<script>

import axios from 'axios';
import { downloadFileToBrowser } from '@/components/Common/BrowserDownload.js';
import { getExportMessage } from '@/components/Common/ExportMessage.js';
import CourseSectionsSelector from '@/components/CourseSectionsSelector';
import selectListOptionsDataContext from '@/services/selectListOptions.dataContext.js';
import ReportTemplates from '@/components/mixins/ReportTemplates.js';
import UserReportTemplate from '@/components/UserReportTemplate.vue';

export default {
    mixins: [ReportTemplates],
    props: {
        termId: {
            type: Number,
            required: false
        },
    },
    data() {
        return {            
            selectedTermId: null,
            isBusy: false,
            // Identifies which report saved templates belong to; must match the report name in Reports.vue.
            reportName: 'Course List',
            reportType: 'pdf',
            courseListParameters: {
                termId: null,
                includeRetailPrices: false,
                includeStockOnHand: false,
                includeQtp: false,
                includeEnrollment: false,
                includePriceDisclaimer: false,
                includeIsbn: false,
                newPagePerDepartment: false,
                courseSections: []
            }
        };
    },
    components: {
         CourseSectionsSelector,
         UserReportTemplate
    },
    computed:{
        // ReportTemplates works against `reportParameters`; this report's parameters object is
        // `courseListParameters`. Returning it here means the mixin saves and restores the same
        // object the inputs are bound to.
        reportParameters() {
            return this.courseListParameters;
        },
        exportCourseListLabel(){
            return `Export Course List (${this.reportType.toUpperCase()})`;
        },
        mockCourseRequest(){
            return {termId: this.termId || this.selectedTermId}
        },
        courseSectionSelectHelpText() {
            if(!this.termId && !this.selectedTermId){
                return 'Please select a term to select sections from.'
            }
            return this.courseListParameters.courseSections.length === 0
                ? 'ALL sections for this term will be included in the course list, you may select individual sections below.'
                : '';
        }
    },
    methods: {            
        async exportCourseListReport(bvModalEvt) {            
            bvModalEvt.preventDefault();
            if (!(await this.$refs.courseListForm.validate())) {
                return;
            }

            this.courseListParameters.termId = this.termId || this.selectedTermId;
            this.isBusy = true;

            if (!(await this.saveUserReportTemplate())) {
                this.isBusy = false;
                return;
            }            

            axios
                .post('/terms/exportCourseList', this.courseListParameters, {
                    responseType: 'blob'
                })
                .then(result => {
                    downloadFileToBrowser(
                        result.data,
                        'Course List',
                        'application/pdf'
                    );

                    // The interceptor raises the api's own message, so only claim success without one.
                    if (!getExportMessage(result)) {
                        this.$appSuccess(
                            `Course list report generated successfully.`
                        )
                    }

                    this.$refs.exportCourseListModal.hide();
                })
                .finally(() => {
                    this.isBusy = false;
                    this.courseListParameters.courseSections = [];
                });
        },
        initialize(){
            this.selectedTermId = null;
            this.courseListParameters = {
                termId: null,
                includeRetailPrices: false,
                includeStockOnHand: false,
                includeQtp: false,
                includeEnrollment: false,
                includePriceDisclaimer: false,
                includeIsbn: false,
                newPagePerDepartment: false,
                courseSections: []
            }
        },
        async show(reportType = 'pdf') {
            this.reportType = reportType;
            await this.loadUserReportTemplates();
            this.$refs.exportCourseListModal.show();
        },
        // ReportTemplates hook: return false when anything in the saved template can no longer be
        // selected, and the mixin warns and loads nothing. The term and the course sections are
        // the saved references — the include/exclude flags are literal values.
        async validateReportParameters(savedParameters) {
            const [terms, courses] = await Promise.all([
                selectListOptionsDataContext.getStoreDropdownData('terms'),
                selectListOptionsDataContext.getStoreDropdownData('courses')
            ]);

            if (savedParameters.termId && !terms.some(term => term.value == savedParameters.termId)) {
                return false;
            }

            // Each saved section must still exist on its course, under the same term and the same
            // department — a course moved to another department would no longer appear under the
            // saved branch of the sections tree.
            return (savedParameters.courseSections ?? []).every(savedSection => {
                const course = courses.find(x => x.value == savedSection.courseId);

                return course
                    && course.filter == savedSection.courseDepartmentId
                    && (course.sections ?? []).some(section =>
                        section.sectionName === savedSection.section
                        && section.termId == savedSection.termId);
            });
        },
        // ReportTemplates hook: the term lives in `selectedTermId` (courseListParameters only gets
        // termId at export time) and CourseSectionsSelector owns its own tree selection, so both
        // have to be pushed back after a restore.
        onUserReportTemplateApplied() {
            this.selectedTermId = this.courseListParameters.termId;

            // The sections tree is filtered by the selected term, so wait for that prop to reach
            // the selector before restoring into it.
            this.$nextTick(() => {
                this.$refs.courseSectionsSelector?.setSelection(this.courseListParameters.courseSections);
            });
        }
    }
};

</script>
